<template>
  <div v-if="feedbackSubmitted" class="flex gap-2 items-center justify-center bg-primary6 pa-2">
    <CIcon class="bg-success color-base rounded-full pa-1" icon="check" :size="10" />
    <span>{{ t('canvasUI.components.CRatingFeedback.feedbackMessage') }}</span>
  </div>
  <div v-else class="c-rating-feedback flex flex-col gap-2">
    <div class="rating">
      <p class="w-full font-600 ma-0">{{ t('canvasUI.components.CRatingFeedback.rating') }}</p>
      <CRate clearable :color="themeVars.warning" size="small" :value="rating" @update:value="updateRating" />
    </div>
    <div class="comment">
      <p class="w-full font-600 ma-0">{{ t('canvasUI.components.CRatingFeedback.review') }}</p>
      <CInput
        :autosize="{ minRows: 3, maxRows: 3 }"
        class="rounded-1 border-primary6 text-sm"
        :placeholder="t('canvasUI.components.CRatingFeedback.reviewPlaceholder')"
        :rows="3"
        :style="{
          '--n-border': `1px solid ${themeVars.primary5}`,
          '--n-caret-color': themeVars.text,
          '--n-border-hover': `1px solid ${themeVars.primary5}`,
          '--n-border-focus': `1px solid ${themeVars.primary5}`,
        }"
        type="textarea"
        :value="comment"
        @update:value="updateComment"
      />
    </div>
    <div class="flex justify-start gap-2">
      <CButton class="color-text3" size="small" @click="clear">
        {{ t('canvasUI.common.clear') }}
      </CButton>
      <CButton :disabled="!isSubmitEnabled" size="small" type="primary" @click="submit">
        {{ t('canvasUI.common.submit') }}
      </CButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import CRate from '@lib/components/CRate/CRate.vue'

import useThemeVars from '@lib/composables/useThemeVars'

const props = withDefaults(
  defineProps<{
    comment: string
    rating: number | null
    notificationDuration?: number
  }>(),
  {
    comment: '',
    rating: null,
    notificationDuration: 3000,
  },
)

const emit = defineEmits<{
  (e: 'update:comment', v: string): void
  (e: 'update:rating', v: number | null): void
  (e: 'submit', v: { rating: number | null; comment: string }): void
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const feedbackSubmitted = ref(false)

const isSubmitEnabled = computed(() => {
  return (props.comment && props.comment.trim() !== '') || (props.rating !== null && props.rating > 0)
})

function updateComment(value: string) {
  emit('update:comment', value)
}

function updateRating(value: number | null) {
  emit('update:rating', value)
}

function clear() {
  updateComment('')
  updateRating(null)
}

function submit() {
  feedbackSubmitted.value = true
  emit('submit', { comment: props.comment, rating: props.rating })
  setTimeout(() => {
    feedbackSubmitted.value = false
    clear()
  }, props.notificationDuration)
}
</script>
